<script setup lang="ts">
import type { SetupContext, Slot, SlotsType } from 'vue'

import LinePlusCircle from '@docue/docue-ui-v2/icons/LinePlusCircle.vue'
import LineInfoCircle from '@docue/docue-ui-v2/icons/LineInfoCircle.vue'
import LineCheckCircle from '@docue/docue-ui-v2/icons/LineCheckCircle.vue'
import { UiTooltip, UiTooltipContent, UiTooltipTrigger } from '#components'

import type { SubscriptionPaymentPeriodUnit } from '~/types'
import type { PriceListFeature, PriceListItemPrice } from '~/composables/api/price-list/useFetchPriceList'

defineOptions({ inheritAttrs: false })

const {
  priceList,
  paymentPeriodUnit,
  selectedPlan,
  planSelected,
} = useSubscriptionProcessContext()
const { formatCurrency } = useIntl()
const { t } = useI18n()
const { country } = useAuth()

const { data: config } = useQueryConfig.current()

const isFeatureListModalOpen = ref(false)

const allFeatures = computed(() => priceList.data.value?.data.features ?? [])
const priceListItem = computed(() => (priceList.data.value?.items ?? [])?.[0])
const promotedFeatures = computed(() => priceListItem.value?.data.promoted_features)
const visibleFeatures = computed(() => allFeatures.value.filter(feature => promotedFeatures.value?.includes(feature.id) && feature.id !== 'monitor'))

const prices = computed(() =>
  ['month', 'year'].map(periodUnit => priceListItem.value?.prices.find(({ period_unit }) => period_unit === periodUnit)).filter(Boolean) as PriceListItemPrice[],
)

const annualPriceListItemPrice = computed(() => prices.value.find(({ period_unit }) => period_unit === 'year'))
const monthlyPriceListItemPrice = computed(() => prices.value.find(({ period_unit }) => period_unit === 'month'))

const annualPriceFormatted = computed(() => formatCurrency((annualPriceListItemPrice.value?.monthly_price ?? 0) / 100, { minimumFractionDigits: 0, maximumFractionDigits: 2 }))
const monthlyPriceFormatted = computed(() => formatCurrency((monthlyPriceListItemPrice.value?.monthly_price ?? 0) / 100, { minimumFractionDigits: 0, maximumFractionDigits: 2 }))

const selectPlan = (periodUnit: SubscriptionPaymentPeriodUnit) => {
  paymentPeriodUnit.value = periodUnit
  selectedPlan.value = priceListItem.value

  const price = periodUnit === 'month' ? (monthlyPriceListItemPrice.value?.monthly_price ?? 0) / 100 : (annualPriceListItemPrice.value?.monthly_price ?? 0 * 12) / 100
  const priceId = periodUnit === 'month' ? monthlyPriceListItemPrice.value?.id : annualPriceListItemPrice.value?.id

  planSelected({
    plan: priceListItem.value?.title,
    price,
    priceId: priceId ?? 'Unknown',
    currency: config.value?.currency_code,
  })
}

const FeatureTooltip = (_: any, { slots }: SetupContext<any, SlotsType<{ default: Slot }>>) => h(UiTooltip, {}, {
  default: () => [
    h(UiTooltipTrigger, { as: 'span', class: 'inline-block size-4 align-middle' }, { default: () => [
      h(LineInfoCircle, { class: 'size-4 text-gray-400' }),
    ] }),
    h(UiTooltipContent, { class: 'max-w-xs text-xs text-gray-600 !z-[100]' }, { default: () => slots.default() }),
  ],
})

const ItemFeatureValue = ({ feature }: { feature: PriceListFeature }, { slots }: SetupContext<any, SlotsType<{ default: Slot<{ featureValue: any }> }>>) => {
  const itemFeature = priceListItem.value?.data.features?.find(({ id }) => id === feature.id)
  const featureValue = itemFeature?.value

  if (!featureValue)
    return null

  return slots.default({ featureValue })
}
</script>

<template>
  <DTModal
    v-model="isFeatureListModalOpen"
  >
    <DTModalHeader class="!border-b-0" />
    <DTModalBody class="!pt-0 lg:!max-h-[50dvh]">
      <ul
        class="mb-10 flex flex-col gap-3"
      >
        <ItemFeatureValue
          v-for="feature in allFeatures"
          :key="feature.id"
          #default="{ featureValue }"
          :feature
        >
          <li class="inline-flex gap-2 text-sm">
            <LineCheckCircle
              v-if="typeof featureValue === 'boolean' && featureValue"
              class="size-5 shrink-0 text-green-500"
            />
            <LinePlusCircle
              v-else
              class="size-5 shrink-0 text-gray-400"
            />

            <div class="flex flex-col gap-1">
              <span class="font-medium">{{ feature.title }}</span>
              <p class="inline text-xs text-gray-600 empty:hidden">
                {{ feature.description }}
                <strong
                  v-if="typeof featureValue !== 'boolean'"
                  class="inline text-xs font-medium text-gray-600 empty:hidden"
                >
                  {{ featureValue }}
                </strong>
              </p>
            </div>
          </li>
        </ItemFeatureValue>
      </ul>
    </DTModalBody>
  </DTModal>

  <div
    v-if="priceListItem"
    class="flex w-full flex-col items-center justify-center gap-4 md:flex-row"
    v-bind="$attrs"
  >
    <UiCard
      v-for="price in prices"
      :key="price.id"
      class="relative w-full max-w-[380px] rounded-xl border-0 drop-shadow-lg"
      :class="{ '!pt-4': country === 'GB' }"
    >
      <UiCardHeader class="flex flex-col items-center gap-5 p-5">
        <div
          v-if="price.period_unit === 'month' && country === 'GB'"
          class="absolute inset-x-0 top-0 mx-auto w-full rounded-t-lg bg-green py-1 text-center text-xs text-white sm:text-sm"
        >
          {{ t('subscriptionPlanCard.monthlySubscriptionLabel') }}
        </div>
        <UiCardTitle class="text-2xl font-bold">
          {{ price.period_unit === 'month' ? t('subscriptionPlanCard.monthlySubscription') : t('subscriptionPlanCard.annualSubscription') }}
        </UiCardTitle>
        <div class="flex flex-col items-center gap-2">
          <span class="text-5xl font-bold">{{ price.period_unit === 'month' ? monthlyPriceFormatted : annualPriceFormatted }}</span>
          <span class="text-sm text-gray-600">{{ t('subscriptionPlanCard.perUserMonth') }}</span>
        </div>
        <UiButton
          color="primary"
          size="xl"
          class="w-full text-base font-medium"
          @click="selectPlan(price.period_unit as SubscriptionPaymentPeriodUnit)"
        >
          {{ t('select') }}
        </UiButton>
      </UiCardHeader>
      <UiCardContent class="flex flex-col gap-2 p-5 pt-0">
        <ul>
          <li
            v-for="feature in visibleFeatures"
            :key="feature.id"
            class="flex items-center gap-3 py-1.5"
          >
            <LineCheckCircle class="size-5 text-green-500" />
            <span class="text-sm text-gray-600">
              <span class="mr-2">{{ feature.title }}</span>
              <FeatureTooltip v-if="feature.description">
                {{ feature.description }}
              </FeatureTooltip>
            </span>
          </li>
        </ul>

        <UiButton
          variant="link"
          color="primary"
          size="xs"
          class="max-w-max p-0 text-sm"
          @click="isFeatureListModalOpen = true"
        >
          {{ t('subscriptionPlanCard.showAllFeatures') }}
        </UiButton>
      </UiCardContent>
    </UiCard>
  </div>
</template>
